import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import EntityService from '../../services/EntityService';
import { API } from '../../shared/api/Api';

/**
 * Fetch all payments
 */
export const fetchPayments = createAsyncThunk(
  'payments/fetchPayments',
  async ({ token, params }, { rejectWithValue }) => {
    try {
      const response = await EntityService.getAllEnties(
        token,
        API.Payments.app_courses_payments_all,
        params
      );
      if (response.data.status === 200 && response.data.errorCode === 0) {
        return {
          items: response.data.data.items,
          currentPage: response.data.data.current_page,
          totalPages: response.data.data.last_page,
          total: response.data.data.total,
        };
      }
      return rejectWithValue(response.data.message || 'Failed to fetch payments');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch payments');
    }
  }
);

/**
 * Fetch a single payment by ID
 */
export const fetchPaymentById = createAsyncThunk(
  'payments/fetchPaymentById',
  async ({ token, paymentId }, { rejectWithValue }) => {
    try {
      const response = await EntityService.getEntity(
        token,
        API.Payments.app_courses_payments_get,
        paymentId
      );
      if (response.data.status === 200 && response.data.errorCode === 0) {
        return {
          ...response.data.data,
          status: response.data.data.status === "verified" ? true : false,
        };
      }
      return rejectWithValue(response.data.message || 'Failed to fetch payment');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch payment');
    }
  }
);

/**
 * Create a new payment
 */
export const createPayment = createAsyncThunk(
  'payments/createPayment',
  async ({ token, formData }, { rejectWithValue }) => {
    try {
      const data = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'status') {
          data.append('status', value ? 'verified' : 'pending');
        } else {
          data.append(key, value);
        }
      });

      const response = await EntityService.addEntity(
        token,
        API.Payments.app_courses_payments_add,
        data
      );
      if (response.data.status === 200 || response.data.status === 201) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message || 'Failed to create payment');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to create payment';
      const validationErrors = error.response?.data?.data || {};
      return rejectWithValue({ message: errorMessage, errors: validationErrors });
    }
  }
);

/**
 * Update an existing payment
 */
export const updatePayment = createAsyncThunk(
  'payments/updatePayment',
  async ({ token, paymentId, formData, initialData }, { rejectWithValue }) => {
    try {
      const data = new FormData();
      
      // Only append changed fields
      Object.keys(formData).forEach((key) => {
        const currentValue = formData[key];
        const initialValue = initialData[key];

        // Handle status toggle
        if (key === 'status') {
          if (currentValue !== initialValue) {
            data.append('status', currentValue ? 'verified' : 'pending');
          }
        }
        // Handle other changed fields
        else if (currentValue !== initialValue) {
          data.append(key, currentValue);
        }
      });

      const response = await EntityService.updateEntity(
        token,
        API.Payments.app_courses_payments_update,
        paymentId,
        data
      );
      if (response.data.status === 200) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message || 'Failed to update payment');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update payment';
      const validationErrors = error.response?.data?.data || {};
      return rejectWithValue({ message: errorMessage, errors: validationErrors });
    }
  }
);

/**
 * Delete a payment
 */
export const deletePayment = createAsyncThunk(
  'payments/deletePayment',
  async ({ token, paymentId }, { rejectWithValue }) => {
    try {
      const response = await EntityService.deleteEntity(
        token,
        API.Payments.app_courses_payments_delete,
        paymentId
      );
      if (response.data.status === 200) {
        return paymentId;
      }
      return rejectWithValue(response.data.message || 'Failed to delete payment');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete payment');
    }
  }
);

const initialState = {
  entities: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    total: 0,
  },
  selectedEntity: null,
  initialFormData: null,
  listStatus: 'idle',
  detailStatus: 'idle',
  createStatus: 'idle',
  updateStatus: 'idle',
  deleteStatus: 'idle',
  listError: null,
  detailError: null,
  createError: null,
  updateError: null,
  deleteError: null,
  formErrors: {},
};

const paymentSlice = createSlice({
  name: 'payments',
  initialState,
  reducers: {
    clearSelectedEntity: (state) => {
      state.selectedEntity = null;
      state.initialFormData = null;
      state.detailStatus = 'idle';
      state.detailError = null;
      state.formErrors = {};
    },
    clearFormErrors: (state) => {
      state.formErrors = {};
      state.createError = null;
      state.updateError = null;
    },
    resetSubmissionStatus: (state) => {
      state.createStatus = 'idle';
      state.updateStatus = 'idle';
      state.createError = null;
      state.updateError = null;
      state.formErrors = {};
    },
    updateFormData: (state, action) => {
      if (state.selectedEntity) {
        state.selectedEntity = {
          ...state.selectedEntity,
          ...action.payload,
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPayments.pending, (state) => {
        state.listStatus = 'loading';
        state.listError = null;
      })
      .addCase(fetchPayments.fulfilled, (state, action) => {
        state.listStatus = 'succeeded';
        state.entities = action.payload.items;
        state.pagination = {
          currentPage: action.payload.currentPage,
          totalPages: action.payload.totalPages,
          total: action.payload.total,
        };
        state.listError = null;
      })
      .addCase(fetchPayments.rejected, (state, action) => {
        state.listStatus = 'failed';
        state.listError = action.payload;
      })
      .addCase(fetchPaymentById.pending, (state) => {
        state.detailStatus = 'loading';
        state.detailError = null;
      })
      .addCase(fetchPaymentById.fulfilled, (state, action) => {
        state.detailStatus = 'succeeded';
        state.selectedEntity = action.payload;
        state.initialFormData = { ...action.payload };
        state.detailError = null;
        state.formErrors = {};
      })
      .addCase(fetchPaymentById.rejected, (state, action) => {
        state.detailStatus = 'failed';
        state.detailError = action.payload;
      })
      .addCase(createPayment.pending, (state) => {
        state.createStatus = 'loading';
        state.createError = null;
        state.formErrors = {};
      })
      .addCase(createPayment.fulfilled, (state) => {
        state.createStatus = 'succeeded';
        state.createError = null;
        state.formErrors = {};
      })
      .addCase(createPayment.rejected, (state, action) => {
        state.createStatus = 'failed';
        const errorData = action.payload;
        state.createError = errorData.message || 'Failed to create payment';
        state.formErrors = errorData.errors || {};
      })
      .addCase(updatePayment.pending, (state) => {
        state.updateStatus = 'loading';
        state.updateError = null;
        state.formErrors = {};
      })
      .addCase(updatePayment.fulfilled, (state, action) => {
        state.updateStatus = 'succeeded';
        state.selectedEntity = action.payload;
        state.initialFormData = { ...action.payload };
        state.updateError = null;
        state.formErrors = {};
      })
      .addCase(updatePayment.rejected, (state, action) => {
        state.updateStatus = 'failed';
        const errorData = action.payload;
        state.updateError = errorData.message || 'Failed to update payment';
        state.formErrors = errorData.errors || {};
      })
      .addCase(deletePayment.pending, (state) => {
        state.deleteStatus = 'loading';
        state.deleteError = null;
      })
      .addCase(deletePayment.fulfilled, (state, action) => {
        state.deleteStatus = 'succeeded';
        state.entities = state.entities.filter(payment => payment.id !== action.payload);
        state.deleteError = null;
      })
      .addCase(deletePayment.rejected, (state, action) => {
        state.deleteStatus = 'failed';
        state.deleteError = action.payload;
      });
  },
});

export const {
  clearSelectedEntity,
  clearFormErrors,
  resetSubmissionStatus,
  updateFormData,
} = paymentSlice.actions;

export const selectPayments = (state) => state.payments.entities;
export const selectPaymentsPagination = (state) => state.payments.pagination;
export const selectSelectedPayment = (state) => state.payments.selectedEntity;
export const selectInitialFormData = (state) => state.payments.initialFormData;
export const selectPaymentsListStatus = (state) => state.payments.listStatus;
export const selectPaymentDetailStatus = (state) => state.payments.detailStatus;
export const selectPaymentCreateStatus = (state) => state.payments.createStatus;
export const selectPaymentUpdateStatus = (state) => state.payments.updateStatus;
export const selectPaymentDeleteStatus = (state) => state.payments.deleteStatus;
export const selectPaymentsListError = (state) => state.payments.listError;
export const selectPaymentDetailError = (state) => state.payments.detailError;
export const selectPaymentCreateError = (state) => state.payments.createError;
export const selectPaymentUpdateError = (state) => state.payments.updateError;
export const selectPaymentDeleteError = (state) => state.payments.deleteError;
export const selectPaymentFormErrors = (state) => state.payments.formErrors;

export default paymentSlice.reducer;
